/** Shared table pagination helpers for native PDF export. */

import {
  PDF_PAGE_CONTENT_HEIGHT_PT,
  PDF_PORTRAIT_CONTENT_HEIGHT_PT,
  PDF_TABLE_FOOTER_SAFETY_PT,
} from './protocolPdfLayout';

import {
  PDF_PORTRAIT_TABLE_MAX_ROWS_PER_CHUNK,
  PDF_SOA_TABLE_MAX_ROWS_PER_CHUNK,
  PDF_TABLE_CELL_PAD_PT,
  PDF_TABLE_CELL_LINE_HEIGHT,
} from './pdfTableLayoutRules';
import { isSoaSectionBannerRow, buildSoaHeaderGroups } from './soaTableExportModel';

export { PDF_PAGE_CONTENT_HEIGHT_PT, PDF_PORTRAIT_CONTENT_HEIGHT_PT };

/** Real per-side cell padding is applied top AND bottom — match that exactly (was a
 * hardcoded 6, undercounting the true 2 * 5 = 10pt and accidentally masking some of the
 * deliberate conservatism elsewhere in these estimates). */
const SOA_CELL_VPAD_PT = PDF_TABLE_CELL_PAD_PT * 2;
/** Match the real rendered line-height (SoaPdfTable's renderCellLines) exactly instead of
 * a separate hardcoded value, so this margin isn't larger than intended. */
const SOA_LINE_HEIGHT_FACTOR = PDF_TABLE_CELL_LINE_HEIGHT;
/** Estimated average character width as a fraction of font size. Deliberately still a bit
 * above the tightest plausible packing (real proportional-font averages run ~0.42-0.48) so
 * an estimate can be wrong by a modest margin without the real text overflowing its budget —
 * SOA chunks are wrap={true} (not atomic), so an occasional under-estimate just flows onto
 * the next page rather than corrupting the layout. */
const SOA_CHAR_WIDTH_FACTOR = 0.46;

/** Slightly taller than render lineHeight so chunk budgets under-fill pages. */
const ROW_HEIGHT_LINE_FACTOR = 1.45;

export const PORTRAIT_TABLE_ROW_HEIGHT_PT = 24;
export const PORTRAIT_TABLE_HEADER_HEIGHT_PT = 28;
/** Prefer packing more rows; height budget is the real limiter. */
export const PORTRAIT_TABLE_ROWS_PER_PAGE = 14;
/** Keep chunks short — oversized wrap={false} chunks squash borders through text. */
export const PORTRAIT_TABLE_MAX_ROWS_PER_CHUNK = PDF_PORTRAIT_TABLE_MAX_ROWS_PER_CHUNK;

export const SOA_TABLE_ROW_HEIGHT_PT = 22;
export const SOA_TABLE_HEADER_HEIGHT_PT = 40;
export const SOA_TABLE_ROWS_PER_PAGE = 6;
/** Keep chunks short so parents never exceed page height and squash text. */
export const SOA_TABLE_MAX_ROWS_PER_CHUNK = PDF_SOA_TABLE_MAX_ROWS_PER_CHUNK;

/** First SOA page also has section title + caption — reserve space. */
export const SOA_FIRST_PAGE_RESERVED_PT = 40;

/** Caption space reserved on the same page as the first portrait table chunk. */
export const PORTRAIT_TABLE_FIRST_PAGE_RESERVED_PT = 36;

/**
 * Conservative header reservation for the row-split budget below. Real SOA headers wrap
 * to 2+ lines per column (visit labels are "period\nday" and are no longer truncated), so
 * the true rendered header is usually taller than SOA_TABLE_HEADER_HEIGHT_PT (a floor used
 * elsewhere). Using the floor here would let a borderline row be judged "fits alone on a
 * page" when the real (taller) header pushes it over — reserve a larger, fixed margin
 * instead, since this is a module-level constant computed before any specific table's
 * columns/fonts are known.
 */
const SOA_HEADER_SPLIT_RESERVE_PT = 70;

/**
 * Usable per-page row budget for SOA chunking — single source of truth shared by
 * chunkSoaTableRows (below) and SOA_MAX_SINGLE_ROW_HEIGHT_PT. These used to be computed
 * independently: chunkSoaTableRows applied a conservative 0.7 fraction of the landscape
 * content height, while the row-split threshold used the full (unscaled) height. That let
 * a row (e.g. one with 2-3 concatenated footnote texts in Key Notes, ~150-200pt tall) fall
 * UNDER the split threshold yet still be far too tall to share chunkSoaTableRows' real
 * per-page budget alongside its header — landing it alone in its own chunk with a repeated
 * header on every page, instead of being split into flowing continuation rows.
 *
 * The 0.7 fraction (now 0.98) was originally over-conservative to compensate for a since-fixed
 * bug: SoaTablePart used to mark each chunk's table wrap={false} ("atomic"), and when many
 * break-forced chunks were rendered in sequence, react-pdf did not reliably relocate an atomic
 * block to a fresh page — instead multiple chunks landed on the same physical page and rendered
 * overlapping/garbled. Now that the table wraps normally (only individual rows stay
 * wrap={false}), a chunk that slightly overflows just flows onto the next page instead of
 * corrupting the layout, so this budget no longer needs the large safety margin — which was
 * also capping every chunk far below what a page can actually hold for short (plain "X")
 * rows, producing many more, thinner pages than the content needed.
 */
const SOA_TABLE_USABLE_PAGE_HEIGHT_PT = Math.floor(
  (PDF_PAGE_CONTENT_HEIGHT_PT - Math.max(28, PDF_TABLE_FOOTER_SAFETY_PT)) * 0.98,
);

/** Max data-row height on one landscape SOA page (below repeating header). */
export const SOA_MAX_SINGLE_ROW_HEIGHT_PT =
  SOA_TABLE_USABLE_PAGE_HEIGHT_PT - SOA_HEADER_SPLIT_RESERVE_PT;

export function estimateTableChunkHeightPt(
  dataRowCount,
  { rowHeight = PORTRAIT_TABLE_ROW_HEIGHT_PT, headerHeight = PORTRAIT_TABLE_HEADER_HEIGHT_PT } = {},
) {
  return headerHeight + Math.max(0, dataRowCount) * rowHeight + 10;
}

function cellPlainText(cell) {
  if (Array.isArray(cell)) return cell.map((r) => r.text || '').join('');
  return String(cell ?? '');
}

/**
 * Count wrapped lines for text that may contain explicit "\n" breaks (e.g. multiple
 * footnotes joined with "\n", or a visit header's "period\nday" pair). Each "\n"-separated
 * segment is its own hard line break AND must still be wrapped by the column's character
 * budget — a 56-character segment in a 20-chars-per-line column is ~3 lines, not 1. Counting
 * only the number of "\n" segments (the previous behavior) silently drops that wrapping and
 * causes chunk budgets to under-report real rendered height, producing Yoga "squash" overlap.
 */
function countWrappedLines(text, charsPerLine) {
  const segments = String(text ?? '').split(/\n/);
  let total = 0;
  segments.forEach((segment) => {
    if (!segment.trim()) return;
    total += Math.max(1, Math.ceil(segment.length / charsPerLine));
  });
  return Math.max(1, total);
}

/** Estimate row height from longest cell text (wrapped lines). */
export function estimatePortraitTableRowHeightPt(row, optionsOrChars = 36) {
  const cells = Array.isArray(row) ? row : [];
  const options =
    typeof optionsOrChars === 'object' && optionsOrChars
      ? optionsOrChars
      : { charsPerLine: optionsOrChars };
  const colWidths = Array.isArray(options.colWidths) ? options.colWidths : null;
  const fontSize = Math.max(8, Number(options.fontSize) || 11);
  const defaultChars = Math.max(8, Number(options.charsPerLine) || 36);
  const pad = 12;

  let maxLines = 1;
  cells.forEach((cell, idx) => {
    const text = cellPlainText(cell).trim();
    if (!text) return;
    const colW = Number(colWidths?.[idx]) > 0 ? Number(colWidths[idx]) : null;
    // 0.55 char width → fewer chars/line → taller estimate (safer vs squash).
    const charsPerLine = colW
      ? Math.max(5, Math.floor((colW - pad) / (fontSize * 0.55)))
      : defaultChars;
    const lines = countWrappedLines(text, charsPerLine);
    maxLines = Math.max(maxLines, lines);
  });

  // No low cap — tall wrapped cells must reserve real space in the chunk budget.
  return pad + maxLines * fontSize * ROW_HEIGHT_LINE_FACTOR;
}

/** SOA rows — estimate from column widths so chunking matches rendered height. */
export function estimateSoaRowHeightPt(rowData, header, colWidths = null, fonts = null) {
  if (!header?.length) return SOA_TABLE_ROW_HEIGHT_PT;

  const lastCol = header.length - 1;
  const cellPad = SOA_CELL_VPAD_PT;
  const isBanner = isSoaSectionBannerRow(rowData, header);
  let maxHeight = SOA_TABLE_ROW_HEIGHT_PT;

  header.forEach((colKey, colIdx) => {
    const text = String(rowData[colKey] ?? '').trim();
    if (!text) return;

    const colWidth = Number(colWidths?.[colIdx]) > 0 ? Number(colWidths[colIdx]) : 60;
    const fontSize =
      colIdx === 0
        ? Number((isBanner ? fonts?.headerPt : fonts?.bodyPt)) || 11
        : colIdx === lastCol
          ? Number(fonts?.footnotePt) || 10
          : Number(fonts?.visitBodyPt) || 11;

    const charsPerLine = Math.max(4, Math.floor((colWidth - cellPad) / (fontSize * SOA_CHAR_WIDTH_FACTOR)));
    const lineCount = countWrappedLines(text, charsPerLine);
    const rowH = cellPad + lineCount * fontSize * SOA_LINE_HEIGHT_FACTOR;
    maxHeight = Math.max(maxHeight, rowH);
  });

  // Key Notes now renders as its own full-width row directly under the activity (see
  // SoaNoteRow in SoaPdfTable.jsx) rather than a narrow trailing column, so it's not part of
  // the per-column loop above. Add its wrapped height on top of the row's own height — using
  // the FULL table width (not one narrow column) means the same footnote text wraps into far
  // fewer lines than the old column layout did.
  const noteText = String(rowData._keyNotesText ?? '').trim();
  if (noteText) {
    const totalWidth = Array.isArray(colWidths) && colWidths.length
      ? colWidths.reduce((sum, w) => sum + (Number(w) || 0), 0)
      : 400;
    const noteFontSize = Number(fonts?.footnotePt) || 10;
    const noteCharsPerLine = Math.max(10, Math.floor((totalWidth - cellPad * 2) / (noteFontSize * SOA_CHAR_WIDTH_FACTOR)));
    const noteLineCount = countWrappedLines(noteText, noteCharsPerLine);
    maxHeight += cellPad + noteLineCount * noteFontSize * SOA_LINE_HEIGHT_FACTOR;
  }

  // No cap here — an artificially capped estimate under-reports real rendered height for
  // long wrapped cells, which lets chunkSoaTableRows/prepareSoaRowsForPdf pack more onto a
  // page than actually fits, producing the Yoga "squash" overlap. Callers that need a
  // page-worth ceiling (e.g. the single-row-alone-in-chunk branch) already handle arbitrarily
  // tall estimates correctly.
  return Math.max(SOA_TABLE_ROW_HEIGHT_PT, maxHeight);
}

function wrappedCellHeightPt(text, widthPt, fontSizePt, cellPad) {
  const t = String(text ?? '').trim();
  if (!t) return 0;
  const charsPerLine = Math.max(4, Math.floor((widthPt - cellPad) / (fontSizePt * SOA_CHAR_WIDTH_FACTOR)));
  const lineCount = countWrappedLines(t, charsPerLine);
  return cellPad + lineCount * fontSizePt * SOA_LINE_HEIGHT_FACTOR;
}

/**
 * SOA header height. Two layouts:
 * - Flat (visitMeta omitted): each header[] entry is one column's full "period\nday" text,
 *   measured at that column's own width — legacy layout, still used when the caller has no
 *   grouping info (e.g. a header shaped for Word).
 * - Grouped (visitMeta provided): mirrors SoaHeaderRow's actual grouped rendering — period
 *   (tier 1) and day (tier 2) ALWAYS render as two separate rows, for every column, not just
 *   ones that share a period with a neighbor. Combining period+day into one cell for a
 *   single-column group seemed like it should be cheaper than two rows, but real SOA data
 *   routinely has long text in BOTH period and day independently (and day is occasionally a
 *   duplicated "X / X" — see s9ColumnLabel) — combining them made that one cell as tall as the
 *   two-row layout would have needed anyway, so multi-span groups' tier-2 row was pure added
 *   height with no offsetting savings, making the "grouped" header taller than the original
 *   flat one. Splitting unconditionally means tier 1 only ever holds short period text and
 *   tier 2 only ever holds one day text (never both concatenated), which is never taller than
 *   the flat layout and often much shorter. Must stay in sync with SoaGroupedHeaderRows.
 */
export function estimateSoaHeaderHeightPt(header, colWidths = null, fonts = null, visitMeta = null) {
  if (!header?.length) return SOA_TABLE_HEADER_HEIGHT_PT;

  const cellPad = SOA_CELL_VPAD_PT;

  if (Array.isArray(visitMeta) && visitMeta.length) {
    const headerPt = Number(fonts?.headerPt) || 10;
    const visitHeaderPt = Number(fonts?.visitHeaderPt) || 10;
    const groups = buildSoaHeaderGroups(visitMeta);

    const labelWidth = Number(colWidths?.[0]) > 0 ? Number(colWidths[0]) : 120;
    let tier1Height = wrappedCellHeightPt(header[0], labelWidth, headerPt, cellPad);
    let tier2Height = 0;

    let colIdx = 1;
    groups.forEach((group) => {
      const groupWidth = colWidths
        ? colWidths.slice(colIdx, colIdx + group.span).reduce((sum, w) => sum + (Number(w) || 0), 0)
        : 60 * group.span;
      const periodText = group.period || group.days[0] || '—';
      tier1Height = Math.max(tier1Height, wrappedCellHeightPt(periodText, groupWidth, visitHeaderPt, cellPad));

      // Only give a group its own tier-2 day text when period is real (non-empty) — if period
      // was empty, its day already stood in for period above and there's nothing left to show.
      if (group.period) {
        group.days.forEach((day, i) => {
          const w = colWidths ? Number(colWidths[colIdx + i]) || groupWidth / group.span : groupWidth / group.span;
          tier2Height = Math.max(tier2Height, wrappedCellHeightPt(day, w, visitHeaderPt, cellPad));
        });
      }
      colIdx += group.span;
    });

    return Math.max(SOA_TABLE_HEADER_HEIGHT_PT, tier1Height + tier2Height);
  }

  const lastCol = header.length - 1;
  let maxHeight = SOA_TABLE_HEADER_HEIGHT_PT;

  header.forEach((label, colIdx) => {
    const isEdge = colIdx === 0 || colIdx === lastCol;
    const colWidth = Number(colWidths?.[colIdx]) > 0 ? Number(colWidths[colIdx]) : 60;
    const fontSize = isEdge ? Number(fonts?.headerPt) || 10 : Number(fonts?.visitHeaderPt) || 10;
    maxHeight = Math.max(maxHeight, wrappedCellHeightPt(label, colWidth, fontSize, cellPad));
  });

  return maxHeight;
}

/**
 * Split body rows into chunks that fit within maxContentHeight (header repeated each chunk).
 * Use firstPageReserved so the first chunk is smaller and stays with preceding caption/text.
 */
export function chunkTableRowsByHeight(
  bodyRows,
  {
    maxContentHeight = PDF_PORTRAIT_CONTENT_HEIGHT_PT,
    headerHeight = PORTRAIT_TABLE_HEADER_HEIGHT_PT,
    estimateRowHeight = estimatePortraitTableRowHeightPt,
    maxRowsPerChunk = PORTRAIT_TABLE_MAX_ROWS_PER_CHUNK,
    fallbackRowsPerPage = PORTRAIT_TABLE_ROWS_PER_PAGE,
    firstPageReserved = 0,
  } = {},
) {
  if (!bodyRows?.length) return [];

  const chunks = [];
  let current = [];
  let isFirstChunk = true;
  let used = headerHeight;

  const flush = () => {
    if (current.length) {
      chunks.push(current);
      isFirstChunk = false;
    }
    current = [];
    used = headerHeight;
  };

  const budget = () =>
    maxContentHeight - (isFirstChunk ? Math.max(0, firstPageReserved) : 0);

  for (const row of bodyRows) {
    const rowH = estimateRowHeight(row);
    const pageBudget = budget();
    const tooTall = used + rowH > pageBudget;
    const tooMany = current.length >= maxRowsPerChunk;

    if (current.length > 0 && (tooTall || tooMany)) {
      flush();
    }

    if (used + rowH > budget() && current.length === 0) {
      chunks.push([row]);
      isFirstChunk = false;
      used = headerHeight;
      continue;
    }

    current.push(row);
    used += rowH;
  }

  flush();

  if (chunks.length) return chunks;

  const fallback = [];
  for (let i = 0; i < bodyRows.length; i += fallbackRowsPerPage) {
    fallback.push(bodyRows.slice(i, i + fallbackRowsPerPage));
  }
  return fallback.length ? fallback : [[]];
}

export function chunkTableRows(rows, rowsPerPage) {
  if (!rows?.length) return [];
  const chunks = [];
  for (let i = 0; i < rows.length; i += rowsPerPage) {
    chunks.push(rows.slice(i, i + rowsPerPage));
  }
  return chunks;
}

export function chunkSoaTableRows(rows, header, options = {}) {
  const {
    maxContentHeight = PDF_PAGE_CONTENT_HEIGHT_PT,
    firstPageReserved = SOA_FIRST_PAGE_RESERVED_PT,
    colWidths = null,
    fonts = null,
    visitMeta = null,
  } = options;

  if (!rows?.length) return [];

  const SAFETY_PT = Math.max(28, PDF_TABLE_FOOTER_SAFETY_PT);
  // Kept in sync with SOA_TABLE_USABLE_PAGE_HEIGHT_PT's fraction above.
  const usable = Math.floor((maxContentHeight - SAFETY_PT) * 0.98);

  // Real header height (labels are no longer truncated, so a fixed 40pt guess
  // under-reports wrapped multi-line visit headers) — never below the floor constant.
  const headerHeight = Math.max(
    SOA_TABLE_HEADER_HEIGHT_PT,
    estimateSoaHeaderHeightPt(header, colWidths, fonts, visitMeta),
  );

  const chunks = [];
  let current = [];
  let used = headerHeight;
  let isFirstChunk = true;

  const pageBudget = () => {
    const reserve = isFirstChunk ? firstPageReserved : 0;
    return usable - reserve;
  };

  const flush = () => {
    if (current.length) {
      chunks.push(current);
      isFirstChunk = false;
    }
    current = [];
    used = headerHeight;
  };

  const estimateRow = (row) => estimateSoaRowHeightPt(row, header, colWidths, fonts);

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const rowH = estimateRow(row);
    const budgetAmt = pageBudget();

    if (rowH > budgetAmt && current.length === 0) {
      chunks.push([row]);
      isFirstChunk = false;
      continue;
    }

    let tooTall = used + rowH > budgetAmt;
    const tooMany = current.length >= SOA_TABLE_MAX_ROWS_PER_CHUNK;

    // Orphan control: don't let a section-banner row become the last row a chunk can
    // fit — that strands it alone on a page with its data rows pushed to the next chunk.
    // Flush early so the banner starts a fresh chunk with room for at least one row.
    if (!tooTall && !tooMany && isSoaSectionBannerRow(row, header)) {
      const nextRow = rows[i + 1];
      const nextRowH = nextRow ? estimateRow(nextRow) : SOA_TABLE_ROW_HEIGHT_PT;
      if (used + rowH + nextRowH > budgetAmt) tooTall = true;
    }

    if (current.length > 0 && (tooTall || tooMany)) {
      flush();
    }

    if (used + rowH > pageBudget() && current.length === 0) {
      chunks.push([row]);
      isFirstChunk = false;
      continue;
    }

    current.push(row);
    used += rowH;
  }

  flush();
  return chunks.length ? chunks : chunkTableRows(rows, SOA_TABLE_ROWS_PER_PAGE);
}
